/**
 * @file Composes each scope level's home summary with its YTD goal: the
 * summaries, `parameters/analytics.calendar`, the caller's teams and the goal
 * documents by id, all read from Firestore, never through a callable.
 * Consumer: `HomePageContent`, which calls it once and hands the result to the
 * Home tiles and Needs me, which reads `pastPromised` and `overdueInvoices`, so one
 * call is the goal paths' first connector and no `permission-denied` is lost.
 * Each level reads its calendar and goal through `useAnalyticsGoalProgress`
 * with its own summary's period, as the Overview panel does with its answer,
 * so Home and the Overview show the same figures. At My sales `booked` is the
 * caller's sales credit (`ANALYTICS_BOOKED_COLUMN`). The team level reads the
 * sum of the caller's teams' goals through `analyticsTeamGoalRead`, as the
 * Overview does; a caller to whom `teamsOfUser` gives no team reads `noTeam`,
 * never a figure.
 */
import { useContext } from 'react';

import type { AnalyticsScopeLevel } from '#types';
import {
  analyticsByLevel,
  analyticsScopeGoalIds,
  analyticsSummaryCount,
  analyticsSummaryFigure,
  analyticsTeamGoalRead,
  useAnalyticsGoalProgress,
  useAnalyticsGoals,
  useAnalyticsUsers,
  type AnalyticsGoalRead,
  type AnalyticsSummaryProgress,
} from '@/analytics';

import { UserContext } from '../../providers/UserContext';
import { useHomeAnalyticsSummary } from './useHomeAnalyticsSummary';

export const useHomeGoalProgress = (): Readonly<
  Record<AnalyticsScopeLevel, AnalyticsSummaryProgress>
> => {
  const { user } = useContext(UserContext);
  const uid = user?.uid ?? '';
  const summaries = useHomeAnalyticsSummary();
  const summaryOf = (level: AnalyticsScopeLevel) => {
    const read = summaries[level];
    return read.status === 'found' ? read.summary : undefined;
  };
  const calendars = {
    self: useAnalyticsGoalProgress(summaryOf('self')),
    team: useAnalyticsGoalProgress(summaryOf('team')),
    org: useAnalyticsGoalProgress(summaryOf('org')),
  };

  const readsTeam = summaries.team.status !== 'notVisible';
  const users = useAnalyticsUsers(readsTeam ? [uid] : []);
  const ownTeams = users.teamsOf(uid);

  const ids = analyticsByLevel((level) => {
    const summary = summaryOf(level);
    const { periodStart } = calendars[level];
    return summary && periodStart !== null
      ? analyticsScopeGoalIds(summary.level, uid, ownTeams, periodStart)
      : [];
  });
  const goals = useAnalyticsGoals([...ids.self, ...ids.team, ...ids.org]);

  return analyticsByLevel((level): AnalyticsSummaryProgress => {
    if (
      level === 'team' &&
      readsTeam &&
      users.resolved &&
      ownTeams.length === 0
    )
      return { status: 'noTeam' };
    const read = summaries[level];
    if (read.status !== 'found') return read;
    const booked = analyticsSummaryFigure(read.summary.booked);
    const quoted = analyticsSummaryFigure(read.summary.quotedByStage);
    const goal: AnalyticsGoalRead =
      level === 'team' && !users.resolved
        ? { status: 'loading' }
        : analyticsTeamGoalRead(ids[level].map(goals));
    return {
      status: 'found',
      booked,
      quoted,
      pastPromised: analyticsSummaryCount(read.summary.pastPromised),
      overdueInvoices: analyticsSummaryCount(read.summary.overdueInvoices),
      progress:
        booked.status === 'answered'
          ? calendars[level].progressOf(goal, booked.decimal, quoted)
          : { status: 'loading' },
    };
  });
};
